import * as React from "react";
import { Link } from "react-router-dom";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { MoreHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Row-level "more options" menu for data tables (design port, ALL-5825).
 *
 * Ported from `.design-ref/prototype/components/ui/row-actions-menu.tsx`
 * (next/link → react-router-dom `Link`; `type="button"` made explicit because
 * the live `Button` carries no default type and these triggers sit inside
 * table cells). No other change to markup or tokens.
 */

export interface RowActionItem {
  label: string;
  /** Renders the item as a link. Mutually exclusive with onSelect. */
  href?: string;
  icon?: React.ReactNode;
  onSelect?: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** Right-aligned helper text, e.g. a shortcut. */
  hint?: string;
  /** Draw a divider above this item to group sections. */
  separatorBefore?: boolean;
}

export interface RowActionsMenuProps {
  /** Identifies the row, e.g. a transfer ID. Becomes the trigger's label. */
  rowLabel: string;
  items: RowActionItem[];
  align?: "start" | "center" | "end";
  className?: string;
}

const ITEM_BASE =
  "flex w-full cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] font-medium outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-45";

/**
 * The row-level "more options" trigger for data tables.
 *
 * Rendered in a portal so the panel is never clipped by the table's
 * `overflow-x-auto` wrapper, and built on Radix so keyboard support
 * (arrows, Home/End, type-ahead, Escape, click-outside) is correct.
 */
export function RowActionsMenu({ rowLabel, items, align = "end", className }: RowActionsMenuProps) {
  return (
    <DropdownMenuPrimitive.Root modal={false}>
      <DropdownMenuPrimitive.Trigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={`Open actions for ${rowLabel}`}
          aria-haspopup="menu"
          className={cn("text-muted hover:text-ink", className)}
        >
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align={align}
          sideOffset={6}
          collisionPadding={12}
          aria-label={`Actions for ${rowLabel}`}
          data-testid="row-actions-menu"
          className="z-50 min-w-[13rem] rounded-lg border border-line bg-surface p-1 shadow-soft outline-none data-[state=open]:animate-[rm-menu-in_120ms_ease-out]"
        >
          {items.map((item, i) => {
            const tone = item.destructive
              ? "text-danger data-[highlighted]:bg-danger-soft"
              : "text-text data-[highlighted]:bg-selected-soft data-[highlighted]:text-ink";

            const content = (
              <>
                {item.icon ? (
                  <span aria-hidden className="grid size-4 shrink-0 place-items-center text-current">
                    {item.icon}
                  </span>
                ) : null}
                <span className="flex-1 truncate">{item.label}</span>
                {item.hint ? <span className="shrink-0 text-xs text-subtle">{item.hint}</span> : null}
              </>
            );

            return (
              <React.Fragment key={`${item.label}-${i}`}>
                {item.separatorBefore ? (
                  <DropdownMenuPrimitive.Separator className="my-1 h-px bg-line" />
                ) : null}

                {item.href ? (
                  <DropdownMenuPrimitive.Item asChild disabled={item.disabled} className={cn(ITEM_BASE, tone)}>
                    <Link to={item.href}>{content}</Link>
                  </DropdownMenuPrimitive.Item>
                ) : (
                  <DropdownMenuPrimitive.Item
                    disabled={item.disabled}
                    onSelect={item.onSelect}
                    className={cn(ITEM_BASE, tone)}
                  >
                    {content}
                  </DropdownMenuPrimitive.Item>
                )}
              </React.Fragment>
            );
          })}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
